/**
 * Quick add modal for product cards.
 * Cards with several variants render a [data-quick-add-trigger] button. Clicking it opens
 * one shared <dialog>; product data is fetched lazily from /products/{handle}.js and cached.
 */
(function() {
  'use strict';

  if (window.QuickAdd) return;

  const cache = new Map();
  const MAX_QTY = 99;

  const CLOSE_ICON = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';

  // Runs `callback` after the next frame has painted, keeping non-visual work out of the interaction.
  function afterPaint(callback) {
    requestAnimationFrame(() => setTimeout(callback, 0));
  }

  function rootUrl(path) {
    const root = window.Shopify?.routes?.root || '/';
    return root + path;
  }

  function formatMoney(cents) {
    if (window.Money?.format) return window.Money.format(cents);
    return (cents / 100).toFixed(2);
  }

  function resizeImage(src, width) {
    if (!src) return '';
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (_) {
      return src;
    }
  }

  function fetchProduct(handle) {
    if (!cache.has(handle)) {
      const request = fetch(rootUrl(`products/${handle}.js`), { headers: { Accept: 'application/json' } })
        .then((response) => {
          if (!response.ok) throw new Error('Could not load product options');
          return response.json();
        })
        .catch((error) => {
          cache.delete(handle);
          throw error;
        });
      cache.set(handle, request);
    }
    return cache.get(handle);
  }

  // Normalises both the legacy (string[]) and current ({name, values}[]) shapes of product.options.
  function getOptions(product) {
    return product.options.map((option, index) => {
      if (typeof option === 'string') {
        const values = [];
        product.variants.forEach((variant) => {
          const value = variant.options[index];
          if (!values.includes(value)) values.push(value);
        });
        return { name: option, values };
      }
      return { name: option.name, values: option.values || [] };
    });
  }

  function esc(value) {
    return String(value).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
  }

  function isColorOption(name) {
    return /colou?r/i.test(name);
  }

  class QuickAddModal {
    constructor() {
      this.product = null;
      this.options = [];
      this.selected = [];
      this.quantity = 1;
      this.trigger = null;
      this.busy = false;
      this.build();
    }

    build() {
      // Opened non-modally: showModal() makes the rest of the page inert, which costs a style
      // recalc of every element on large collection pages. The overlay, Escape and focus trap
      // below stand in for what the modal top layer would give us.
      const overlay = document.createElement('div');
      overlay.className = 'quick-add-overlay';
      overlay.hidden = true;
      document.body.appendChild(overlay);

      const dialog = document.createElement('dialog');
      dialog.className = 'quick-add';
      dialog.setAttribute('aria-modal', 'true');
      dialog.setAttribute('aria-labelledby', 'quick-add-title');
      dialog.innerHTML = `
        <div class="quick-add__panel">
          <button type="button" class="quick-add__close" aria-label="Close">${CLOSE_ICON}</button>
          <div class="quick-add__media"><img class="quick-add__image" alt="" width="600" height="750"></div>
          <div class="quick-add__head">
            <h2 class="quick-add__title" id="quick-add-title"></h2>
            <div class="quick-add__price">
              <span class="quick-add__price-now"></span>
              <s class="quick-add__price-compare" hidden></s>
              <span class="quick-add__price-save" hidden></span>
            </div>
          </div>
          <div class="quick-add__body">
            <div class="quick-add__options" aria-live="polite"></div>
            <div class="quick-add__actions">
              <div class="quick-add__qty" role="group" aria-label="Quantity">
                <button type="button" class="quick-add__qty-btn" data-qty="-1" aria-label="Decrease quantity">&minus;</button>
                <input class="quick-add__qty-input" type="text" inputmode="numeric" value="1" aria-label="Quantity">
                <button type="button" class="quick-add__qty-btn" data-qty="1" aria-label="Increase quantity">+</button>
              </div>
              <button type="button" class="quick-add__submit" disabled>
                <span class="quick-add__submit-text">Add to cart</span>
                <span class="quick-add__spinner" aria-hidden="true"></span>
              </button>
            </div>
            <p class="quick-add__error" role="alert" hidden></p>
            <a class="quick-add__details" href="#">View full details</a>
          </div>
        </div>`;
      document.body.appendChild(dialog);

      this.dialog = dialog;
      this.overlay = overlay;
      this.imageEl = dialog.querySelector('.quick-add__image');
      this.titleEl = dialog.querySelector('.quick-add__title');
      this.priceNow = dialog.querySelector('.quick-add__price-now');
      this.priceCompare = dialog.querySelector('.quick-add__price-compare');
      this.priceSave = dialog.querySelector('.quick-add__price-save');
      this.optionsEl = dialog.querySelector('.quick-add__options');
      this.qtyInput = dialog.querySelector('.quick-add__qty-input');
      this.submitBtn = dialog.querySelector('.quick-add__submit');
      this.submitText = dialog.querySelector('.quick-add__submit-text');
      this.errorEl = dialog.querySelector('.quick-add__error');
      this.detailsLink = dialog.querySelector('.quick-add__details');

      overlay.addEventListener('click', () => this.close());
      dialog.addEventListener('keydown', (event) => this.onKeydown(event));
      dialog.addEventListener('close', () => this.onClosed());
      dialog.querySelector('.quick-add__close').addEventListener('click', () => this.close());
      this.optionsEl.addEventListener('change', (event) => this.onOptionChange(event));
      this.submitBtn.addEventListener('click', () => this.submit());
      this.qtyInput.addEventListener('change', () => this.setQuantity(this.qtyInput.value));
      dialog.querySelectorAll('.quick-add__qty-btn').forEach((button) => {
        button.addEventListener('click', () => this.setQuantity(this.quantity + Number(button.dataset.qty)));
      });
    }

    open(trigger) {
      const handle = trigger.dataset.productHandle;
      if (!handle) return;

      this.trigger = trigger;
      this.product = null;
      this.quantity = 1;
      this.qtyInput.value = '1';
      this.hideError();

      this.titleEl.textContent = trigger.dataset.productTitle || '';
      this.imageEl.src = trigger.dataset.productImage || '';
      this.imageEl.alt = trigger.dataset.productTitle || '';
      this.priceNow.textContent = '';
      this.priceCompare.hidden = true;
      this.priceSave.hidden = true;
      this.detailsLink.href = trigger.dataset.productUrl || rootUrl(`products/${handle}`);
      this.optionsEl.innerHTML = '<div class="quick-add__skeleton"></div><div class="quick-add__skeleton quick-add__skeleton--short"></div>';
      this.setSubmit('Add to cart', true);

      if (!this.dialog.open) {
        this.overlay.hidden = false;
        this.dialog.show();
        this.dialog.querySelector('.quick-add__close').focus({ preventScroll: true });
      }

      // Locking page scroll relayouts the whole page, so do it after the modal has painted.
      afterPaint(() => {
        if (this.dialog.open) document.documentElement.classList.add('quick-add-open');
      });

      fetchProduct(handle).then((product) => {
        if (this.trigger !== trigger) return;
        this.load(product);
      }).catch((error) => {
        if (this.trigger !== trigger) return;
        this.optionsEl.innerHTML = '';
        this.showError(error.message || 'Could not load product options');
      });
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    // Escape to close and a Tab loop, which a non-modal dialog doesn't provide on its own.
    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = Array.from(
        this.dialog.querySelectorAll('button:not([disabled]), input:not([disabled]), a[href]')
      ).filter((element) => element.getClientRects().length > 0);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onClosed() {
      this.overlay.hidden = true;
      this.trigger?.focus?.({ preventScroll: true });
      this.trigger = null;
      afterPaint(() => {
        if (!this.dialog.open) document.documentElement.classList.remove('quick-add-open');
      });
    }

    load(product) {
      this.product = product;
      this.options = getOptions(product);

      const start = product.variants.find((variant) => variant.available) || product.variants[0];
      this.selected = this.options.map((_, index) => start.options[index]);

      this.renderOptions();
      this.update();
    }

    getVariant() {
      return this.product?.variants.find((variant) =>
        variant.options.every((value, index) => value === this.selected[index])
      ) || null;
    }

    // Whether choosing `value` for option `index` leaves at least one purchasable variant.
    isValueAvailable(index, value) {
      return this.product.variants.some((variant) => {
        if (!variant.available || variant.options[index] !== value) return false;
        return variant.options.every((other, i) => i === index || other === this.selected[i]);
      });
    }

    swatchStyle(optionIndex, value) {
      if (window.CSS?.supports?.('color', value.toLowerCase().replace(/\s+/g, ''))) {
        return `background-color:${value.toLowerCase().replace(/\s+/g, '')}`;
      }
      const variant = this.product.variants.find((item) => item.options[optionIndex] === value && item.featured_image);
      if (variant) return `background-image:url('${resizeImage(variant.featured_image.src, 80)}')`;
      return '';
    }

    renderOptions() {
      const hasRealOptions = !(this.options.length === 1 && this.options[0].values.length === 1 && this.options[0].values[0] === 'Default Title');
      if (!hasRealOptions) {
        this.optionsEl.innerHTML = '';
        return;
      }

      this.optionsEl.innerHTML = this.options.map((option, index) => {
        const swatch = isColorOption(option.name);
        const values = option.values.map((value) => {
          const id = `quick-add-${index}-${value.replace(/\W+/g, '-')}`;
          const style = swatch ? this.swatchStyle(index, value) : '';
          const safe = esc(value);
          const label = swatch && style
            ? `<span class="quick-add__swatch" style="${esc(style)}"></span><span class="visually-hidden">${safe}</span>`
            : `<span class="quick-add__pill-text">${safe}</span>`;
          return `
            <input class="quick-add__radio" type="radio" name="quick-add-option-${index}" id="${id}" value="${safe}" data-index="${index}">
            <label class="quick-add__choice${swatch && style ? ' quick-add__choice--swatch' : ''}" for="${id}" title="${safe}">${label}</label>`;
        }).join('');

        return `
          <fieldset class="quick-add__option">
            <legend class="quick-add__legend">${esc(option.name)}: <span class="quick-add__legend-value" data-legend="${index}"></span></legend>
            <div class="quick-add__choices">${values}</div>
          </fieldset>`;
      }).join('');
    }

    onOptionChange(event) {
      const input = event.target.closest('.quick-add__radio');
      if (!input) return;

      const index = Number(input.dataset.index);
      this.selected[index] = input.value;

      if (!this.getVariant()) {
        // Combination doesn't exist: fall back to the closest variant that has the chosen value.
        const fallback = this.product.variants.find((variant) => variant.options[index] === input.value && variant.available)
          || this.product.variants.find((variant) => variant.options[index] === input.value);
        if (fallback) this.selected = fallback.options.slice();
      }

      this.hideError();
      this.update();
    }

    update() {
      const variant = this.getVariant();

      this.optionsEl.querySelectorAll('.quick-add__radio').forEach((input) => {
        const index = Number(input.dataset.index);
        input.checked = this.selected[index] === input.value;
        input.nextElementSibling.classList.toggle('is-unavailable', !this.isValueAvailable(index, input.value));
      });
      this.optionsEl.querySelectorAll('[data-legend]').forEach((el) => {
        el.textContent = this.selected[Number(el.dataset.legend)] || '';
      });

      if (!variant) {
        this.setSubmit('Unavailable', true);
        return;
      }

      this.priceNow.textContent = formatMoney(variant.price);
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      this.priceCompare.hidden = !onSale;
      this.priceSave.hidden = !onSale;
      if (onSale) {
        this.priceCompare.textContent = formatMoney(variant.compare_at_price);
        const percent = Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
        this.priceSave.textContent = `-${percent}%`;
      }

      const imageSrc = variant.featured_image?.src || this.product.featured_image;
      if (imageSrc) {
        const next = resizeImage(imageSrc, 900);
        if (this.imageEl.src !== next) this.imageEl.src = next;
      }

      this.setSubmit(variant.available ? 'Add to cart' : 'Sold out', !variant.available);
    }

    setSubmit(text, disabled) {
      this.submitText.textContent = text;
      this.submitBtn.disabled = disabled || this.busy;
    }

    setQuantity(value) {
      const parsed = Number.parseInt(value, 10);
      this.quantity = Number.isNaN(parsed) ? 1 : Math.min(Math.max(parsed, 1), MAX_QTY);
      this.qtyInput.value = String(this.quantity);
    }

    showError(message) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    hideError() {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }

    async submit() {
      const variant = this.getVariant();
      if (!variant || !variant.available || this.busy) return;

      this.busy = true;
      this.submitBtn.disabled = true;
      this.submitBtn.classList.add('is-loading');
      this.hideError();

      const formData = new FormData();
      formData.append('id', variant.id);
      formData.append('quantity', String(this.quantity));

      let drawerOpened = false;
      // When a cart drawer can show its "adding" state, close the modal first so the drawer
      // opens instantly in front (the quick add sits above the theme's drawers).
      const canShowDrawer = Boolean(
        window.CartApi?.beginDrawerAdd &&
          (window.CustomHeaderCart?.showAdding || window.SimpleHeaderCart?.showAdding)
      );

      try {
        if (canShowDrawer) this.close();
        drawerOpened = (await window.CartApi?.beginDrawerAdd?.()) || false;
        const response = window.CartApi?.addToCart
          ? await CartApi.addToCart(formData)
          : await this.fallbackAdd(formData);
        const cart = window.CartApi?.refreshCart ? await CartApi.refreshCart(response) : response;

        this.close();
        document.dispatchEvent(new CustomEvent('cart:added', { detail: cart || response }));

        if (!drawerOpened && !window.SimpleHeaderCart?.open) {
          const cartTrigger =
            document.getElementById('simple-header-cart-trigger') ||
            document.getElementById('custom-header-cart-trigger');
          cartTrigger?.click();
        }
      } catch (error) {
        if (drawerOpened) CartApi.cancelDrawerAdd();
        const message = error.message || 'Could not add to cart';
        if (this.dialog.open) {
          this.showError(message);
        } else if (window.CartApi?.showToast) {
          CartApi.showToast(message, true);
        }
      } finally {
        this.busy = false;
        this.submitBtn.classList.remove('is-loading');
        this.update();
      }
    }

    async fallbackAdd(formData) {
      const response = await fetch(rootUrl('cart/add.js'), {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || 'Could not add to cart');
      }
      return response.json();
    }
  }

  let modal = null;

  window.QuickAdd = {
    open(trigger) {
      if (!modal) modal = new QuickAddModal();
      modal.open(trigger);
    }
  };

  // Build the dialog while the browser is idle so the first tap only has to show it.
  function prebuild() {
    if (!modal) modal = new QuickAddModal();
  }
  window.addEventListener('load', () => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(prebuild, { timeout: 3000 });
    } else {
      setTimeout(prebuild, 1500);
    }
  }, { once: true });

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('[data-quick-add-trigger]');
    if (!trigger) return;
    event.preventDefault();
    window.QuickAdd.open(trigger);
  });
})();
